// TODO enable eslint
/* eslint-disable react/jsx-curly-brace-presence */
import React from "react"
import DatePicker from "rc-calendar/lib/Picker"
import ClickOutside from "react-click-outside"
import cn from "classnames"
import { compose, withStateHandlers, withHandlers, withProps } from "recompact"
import { withSize } from "react-sizeme"
import storage from "src/helpers/storage"
import "rc-calendar/assets/index.css"
import ruRu from "rc-calendar/lib/locale/ru_RU"
import DownFillIcon from "src/icons/downFill.svg"
import Calendar from "./components/Calendar"
import MonthCalendar from "./components/MonthCalendar"
import YearCalendar from "./components/YearCalendar"
import { URL_DATE_FORMAT, LAST_SELECTED_DATES } from "../../constants"
import styles from "./styles.css"

const toTitleCase = phrase => {
  return phrase
    .toLowerCase()
    .split(` `)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(` `)
}

const ToggleDatePickerButton = ({ placeholder, handleToggle, isOpen }) => (
  <button
    type="button"
    onClick={handleToggle}
    className={cn(styles.Button, { [styles.isOpen]: isOpen })}
  >
    <span>{toTitleCase(placeholder)}</span>&nbsp;г.
    <DownFillIcon size="small" color="#000" />
  </button>
)

const renderDate = (defaultValue, isWeek, current) => {
  if (
    current &&
    defaultValue &&
    current.year() === defaultValue.year() &&
    current.week() === defaultValue.week() &&
    isWeek
  ) {
    return (
      <div className="rc-calendar-selected-day-week">
        <div className="rc-calendar-date">{current.date()}</div>
      </div>
    )
  }
  if (
    current &&
    defaultValue &&
    current.year() === defaultValue.year() &&
    current.month() === defaultValue.month() &&
    current.date() === defaultValue.date()
  ) {
    return (
      <div className="rc-calendar-selected-day-day">
        <div className="rc-calendar-date">{current.date()}</div>
      </div>
    )
  }
  return <div className="rc-calendar-date">{current.date()}</div>
}
/* eslint-disable jsx-a11y/anchor-is-valid */
const renderMonth = (defaultValue, isYear, current) => {
  const format = `MMMM`
  const isSameYear =
    current && defaultValue && current.year() === defaultValue.year()
  if (
    (isYear && isSameYear) ||
    (isSameYear && current.month() === defaultValue.month())
  ) {
    return (
      <a
        href="#"
        className={cn(
          styles[`cell-content`],
          styles[`month-cell-content`],
          styles.current,
        )}
      >
        {current.format(format)}
      </a>
    )
  }
  return (
    <a
      href="#"
      className={cn(styles[`cell-content`], styles[`month-cell-content`])}
    >
      {current.format(format)}
    </a>
  )
}

const renderYear = (defaultValue, current) => {
  const isSameYear =
    current && defaultValue && current.year === defaultValue.year()
  if (isSameYear) {
    return (
      <a
        href="#"
        className={cn(
          styles[`cell-content`],
          styles[`year-cell-content`],
          styles.current,
        )}
      >
        {current.title}
      </a>
    )
  }
  return (
    <a
      href="#"
      className={cn(styles[`cell-content`], styles[`year-cell-content`])}
    >
      {current.title}
    </a>
  )
}

const WrappedCalendar = ({
  marginLeft,
  handleClickOutside,
  mode,
  ...calendarProps
}) => (
  <div
    tabIndex="0" // eslint-disable-line
    className={styles[`calendar-wrapper`]}
    style={{ marginLeft }}
  >
    <ClickOutside onClickOutside={handleClickOutside}>
      {(mode === `date` || mode === `week`) && <Calendar {...calendarProps} />}
      {mode === `month` && <MonthCalendar {...calendarProps} />}
      {mode === `year` && <YearCalendar {...calendarProps} />}
    </ClickOutside>
  </div>
)

const enhance = compose(
  withStateHandlers(
    { isOpen: false },
    {
      handleToggle: ({ isOpen }) => () => ({ isOpen: !isOpen }),
      handleClickOutside: () => () => ({ isOpen: false }),
    },
  ),
  withHandlers({
    handleSelect: ({ handleClickOutside, queryApi, mode }) => date => {
      storage.remove(LAST_SELECTED_DATES)
      const intervalName = mode === `date` ? `day` : mode
      const intervalStart = date.startOf(intervalName)
      const currentUrl = window.location.pathname
      const newUrl = currentUrl.replace(
        /\/calendar\/[^/]+\/[^/]+/,
        `/calendar/${intervalName}/${intervalStart.format(URL_DATE_FORMAT)}`,
      )
      queryApi.push(newUrl)
      handleClickOutside()
    },
  }),
  withProps(
    ({
      mode,
      defaultValue,
      handleSelect,
      handleClickOutside,
      minDate,
      maxDate,
    }) => {
      const calendarProps = {
        mode,
        defaultValue,
        handleClickOutside,
        onSelect: handleSelect,
        showDateInput: false,
        showToday: false,
        disabledDate: current =>
          current &&
          (current.startOf(`day`).valueOf() >= maxDate ||
            current.endOf(`day`).valueOf() <= minDate),
        disabledMonth: current =>
          current &&
          (current.startOf(`month`).valueOf() >= maxDate ||
            current.endOf(`month`).valueOf() <= minDate),
        disabledYear: current =>
          current &&
          (current.startOf(`year`).valueOf() >= maxDate ||
            current.endOf(`year`).valueOf() <= minDate),
        locale: {
          ...ruRu,
          monthFormat: `MMMM`,
        },
        dateRender: renderDate.bind(null, defaultValue, false),
        monthCellRender: renderMonth.bind(null, defaultValue, false),
        yearCellRender: renderYear.bind(null, defaultValue),
      }
      if (mode === `week`) {
        calendarProps.mode = `date`
        calendarProps.className = `week-calendar`
        calendarProps.dateRender = renderDate.bind(null, defaultValue, true)
      }
      return { calendarProps }
    },
  ),
)

const WrappedDatePicker = ({
  calendarProps,
  isOpen,
  handleToggle,
  placeholder,
  size,
  mode,
}) => (
  <div className={styles.Calendar}>
    <DatePicker
      calendar={
        <WrappedCalendar
          {...calendarProps}
          marginLeft={(size.width - 239) / 2}
          mode={mode}
        />
      }
      open={isOpen}
    >
      {() => (
        <ToggleDatePickerButton
          placeholder={placeholder}
          handleToggle={handleToggle}
          isOpen={isOpen}
        />
      )}
    </DatePicker>
  </div>
)

export default enhance(withSize()(WrappedDatePicker))
